/**
 * Suscripción compartida por las dos barras doradas del CRM (`AlwaysScrollbar` y el
 * enhancer global `DialogScrollbars`): avisa cada vez que puede haber cambiado si el
 * contenido desborda o no.
 *
 * Las dos escuchaban solo `scroll` + `ResizeObserver` sobre el scroller y sus hijos
 * DIRECTOS al momento de montar. Eso deja fuera el caso más común en el formulario de
 * propiedades: el contenido crece DESPUÉS de abrir el modal —los atributos de
 * MercadoLibre llegan del server, el wizard cambia de paso y reemplaza sus hijos, las
 * fotos terminan de cargar— y el scroller tiene alto fijo (`flex:1`), así que su propio
 * ResizeObserver nunca dispara. Resultado: la barra quedaba oculta con el contenido ya
 * desbordado y el agente no podía bajar a completar la ficha técnica.
 * (reportado por Evelyn sobre IMP-2026-2320, 28/07/2026)
 *
 * Devuelve la función de limpieza.
 */
export function watchScroller(el: HTMLElement, onChange: () => void): () => void {
  // Las mutaciones del DOM llegan de a ráfagas (cada tecla en un input dispara varias):
  // se agrupan en un frame para no re-medir de más.
  let raf = 0;
  const schedule = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(onChange);
  };

  onChange();

  el.addEventListener('scroll', onChange, { passive: true });
  window.addEventListener('resize', schedule);

  const ro = new ResizeObserver(schedule);
  ro.observe(el);
  // Los hijos se re-observan ante cada cambio de DOM: el wizard reemplaza el contenido
  // entero al cambiar de paso y los nodos nuevos no estaban observados.
  const observeChildren = () => { for (const c of Array.from(el.children)) ro.observe(c); };
  observeChildren();

  const mo = new MutationObserver(() => { observeChildren(); schedule(); });
  mo.observe(el, { childList: true, subtree: true });

  // Una imagen que termina de cargar cambia la altura sin mutar el DOM.
  const onLoad = (e: Event) => { if (e.target instanceof HTMLImageElement) schedule(); };
  el.addEventListener('load', onLoad, true);

  // Al abrir un diálogo el layout todavía se está asentando (animación de entrada):
  // se re-mide un par de veces hasta que el alto real es el definitivo.
  const timers = [80, 260, 600].map((ms) => window.setTimeout(onChange, ms));

  return () => {
    cancelAnimationFrame(raf);
    el.removeEventListener('scroll', onChange);
    el.removeEventListener('load', onLoad, true);
    window.removeEventListener('resize', schedule);
    ro.disconnect();
    mo.disconnect();
    timers.forEach((t) => window.clearTimeout(t));
  };
}
